import { Settings } from 'lucide-react';
import { BottomSheet } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { settingsEntry } from './adminNavigation';
import { NavEntryLink } from './NavEntryLink';
import type { ActivePlace } from './navVisibility';
import type { PhoneNavigation } from './phoneNavigation';

const rowClass =
  'flex min-h-[52px] items-center gap-3 rounded-control px-3 text-[15px] font-bold no-underline';

interface PhoneMoreSheetProps {
  nav: PhoneNavigation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  place: ActivePlace;
}

/** Blatt „Mehr“: die Bereiche, die nicht in die Leiste passen, dazu Einstellungen und Plattform. */
export function PhoneMoreSheet({ nav, open, onOpenChange, place }: PhoneMoreSheetProps) {
  const close = () => onOpenChange(false);
  const row = (active: boolean) =>
    cn(rowClass, active ? 'bg-forest-soft text-ink' : 'bg-paper text-ink');
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title="Mehr">
      <div className="flex flex-col gap-4 pb-2">
        {nav.moreAreas.map((area) => (
          <section key={area.id} aria-label={area.label} className="flex flex-col gap-1.5">
            <p className="text-ink-soft flex items-center gap-2 px-1 font-mono text-[11px] font-semibold uppercase tracking-[0.14em]">
              <area.icon size={14} aria-hidden />
              {area.label}
            </p>
            {area.entries.map((entry) => {
              const active = place?.kind === 'area' && place.entry === entry;
              return (
                <NavEntryLink
                  key={`${entry.to}${JSON.stringify(entry.search ?? {})}`}
                  entry={entry}
                  active={active}
                  onNavigate={close}
                  className={row(active)}
                >
                  {entry.label}
                </NavEntryLink>
              );
            })}
          </section>
        ))}
        {nav.settings && (
          <NavEntryLink
            entry={settingsEntry}
            active={place?.kind === 'settings'}
            onNavigate={close}
            className={row(place?.kind === 'settings')}
          >
            <Settings size={18} aria-hidden />
            Einstellungen
          </NavEntryLink>
        )}
        {nav.platform.length > 0 && (
          <section aria-label="Plattform" className="flex flex-col gap-1.5">
            <p className="text-ink-soft px-1 font-mono text-[11px] font-semibold uppercase tracking-[0.14em]">
              Plattform · nur du
            </p>
            {nav.platform.map((entry) => {
              const active = place?.kind === 'platform' && place.entry === entry;
              return (
                <NavEntryLink
                  key={entry.to}
                  entry={entry}
                  active={active}
                  onNavigate={close}
                  className={row(active)}
                >
                  {entry.label}
                </NavEntryLink>
              );
            })}
          </section>
        )}
      </div>
    </BottomSheet>
  );
}
